import React, { useRef, FC } from 'react';
import Slider from '@mui/material/Slider';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded';
import { VolumeSliderBox, VolumeWraper } from './styles';

interface VolumeControlProps {
  volume: number;
  isMuted: boolean;
  onVolumeChange: (newVolume: number) => void;
  onMuteToggle: (muted: boolean) => void;
}

const VolumeControl: FC<VolumeControlProps> = ({ volume, isMuted, onVolumeChange, onMuteToggle }) => {
  const previousVolume = useRef(1);

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = typeof newValue === 'number' ? newValue : newValue[0];
    onVolumeChange(newVolume);

    if (newVolume > 0 && isMuted) {
      onMuteToggle(false);
    }
  };

  const muteOrUnmute = () => {
    const newMuted = !isMuted;
    const newVolume = newMuted ? 0 : previousVolume.current;

    if (!newMuted) {
      previousVolume.current = volume || 1;
    }

    onVolumeChange(newVolume);
    onMuteToggle(newMuted);
  };

  const renderVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return (
        <VolumeOffRoundedIcon
          sx={{
            cursor: 'pointer',
            zIndex: 2,
            position: 'relative',
          }}
          onClick={muteOrUnmute}
        />
      );
    }
    if (volume < 0.6) {
      return (
        <VolumeDownRoundedIcon
          sx={{
            cursor: 'pointer',
            zIndex: 2,
            position: 'relative',
          }}
          onClick={muteOrUnmute}
        />
      );
    }
    return (
      <VolumeUpRoundedIcon
        sx={{
          cursor: 'pointer',
          zIndex: 2,
          position: 'relative',
        }}
        onClick={muteOrUnmute}
      />
    );
  };

  const preventHorizontalKeyboardNavigation = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
    }
  };

  return (
    <VolumeWraper>
      <VolumeSliderBox id="volume-slider">
        <Slider
          sx={{
            '& input[type="range"]': {
              WebkitAppearance: 'slider-vertical',
            },
            '& .MuiSlider-thumb': {
              '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: 'inherit',
              },
              '&::before': {
                display: 'none',
              },
            },
          }}
          orientation="vertical"
          value={volume}
          onChange={handleVolumeChange}
          onKeyDown={preventHorizontalKeyboardNavigation}
          size="small"
          min={0}
          max={1}
          step={0.01}
        />
      </VolumeSliderBox>
      {renderVolumeIcon()}
    </VolumeWraper>
  );
};

export default VolumeControl;
